// -----------------------------------------------------------------------------
// RecentlyCompletedCard
//
// Compact 30%-width confirmation pane rendered on the LEFT after a user
// completes an action on the Up Next card.  The orchestrator captures the
// completed item + completion label before dispatching refresh(), passes them
// here, then clears `recentlyCompleted` after ~4 s (or on user dismiss).
//
// Props
//   item        — the checklist item that was just completed
//   label       — human-readable past-tense label ("Email sent", "Note added"…)
//   completedAt — JS Date of completion (for relative timestamp)
//   onDismiss   — callback to clear the transition state immediately
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import moment from 'moment';
import { Box, Typography, IconButton, useMediaQuery } from '@mui/material';
import Iconify from 'src/components/Iconify';
import { ACTION_META, DEFAULT_ACTION_META } from './actionMeta';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

export default function RecentlyCompletedCard({ item, label, completedAt, onDismiss }) {
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [appeared, setAppeared] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setAppeared(true), 16);
        return () => clearTimeout(t);
    }, []);

    if (!item) return null;

    const meta   = ACTION_META[item.item_type] || DEFAULT_ACTION_META;
    const title  = item.title || item.name || meta.label;
    const timeAgo = completedAt ? moment(completedAt).fromNow() : 'Just now';

    return (
        <Box
            data-testid="upnext-recently-completed-card"
            sx={{
                position: 'relative',
                bgcolor: '#f0fdf4',
                borderRadius: '16px',
                p: 2.5,
                height: '100%',
                minHeight: 160,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.04)',
                opacity: appeared ? 1 : 0,
                transition: reducedMotion ? 'none' : `opacity 240ms ${EASING}`,
            }}
        >
            {/* Dismiss button */}
            <IconButton
                data-testid="upnext-recently-completed-dismiss"
                size="small"
                onClick={onDismiss}
                sx={{ position: 'absolute', top: 8, right: 8, color: '#86868b' }}
                aria-label="Dismiss confirmation"
            >
                <Iconify icon="mdi:close" sx={{ fontSize: 16 }} />
            </IconButton>

            {/* Check circle */}
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <Iconify icon="mdi:check" sx={{ color: '#fff', fontSize: 22 }} />
            </Box>

            {/* Label + title + time */}
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    data-testid="upnext-recently-completed-label"
                    sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#166534',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        mb: 0.5,
                    }}
                >
                    {label}
                </Typography>

                <Typography
                    data-testid="upnext-recently-completed-title"
                    sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: '#1a1a1a',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {title}
                </Typography>

                <Typography
                    data-testid="upnext-recently-completed-time"
                    sx={{ fontSize: 11, color: '#86868b', mt: 0.5 }}
                >
                    {timeAgo}
                </Typography>
            </Box>
        </Box>
    );
}

RecentlyCompletedCard.propTypes = {
    item:        PropTypes.object.isRequired,
    label:       PropTypes.string.isRequired,
    completedAt: PropTypes.instanceOf(Date),
    onDismiss:   PropTypes.func,
};

RecentlyCompletedCard.defaultProps = {
    completedAt: null,
    onDismiss:   null,
};
